<template>
  <div
    v-if="shouldShowTabs"
    dir="rtl"
    class="w-full mt-5 bg-[#FBFBFB] p-[85px]"
  >
    <!-- Barcode selector (shown when multiple barcodes exist) -->
    <div
      v-if="product.product.barcodes && product.product.barcodes.length > 1"
      class="flex flex-col gap-[4px] px-4 lg:px-8 mb-4 w-full lg:w-1/2"
    >
      סוג המוצר מסופק באקראי ללא אפשרות בחירה.
      <br />
      את הפירוט אודות כל סוג תוכלו לראות כאן.
      <p class="text-grays-600 text-sm font-medium">הצג את פרטי המוצר של:</p>
      <select
        v-model="selectedBarcodeIndex"
        class="border border-grays-400 rounded text-grays-900 text-base px-3 py-2 bg-white w-full lg:w-auto"
      >
        <option
          v-for="(label, index) in barcodeOptions"
          :key="index"
          :value="index"
        >
          {{ label }}
        </option>
      </select>
    </div>

    <!-- Tabs Navigation -->
    <div
      class="flex flex-wrap justify-start gap-4 lg:gap-8 px-[15px] my-4 border-b-2 border-[#EAE7F9]"
    >
      <button
        v-for="tab in availableTabs"
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="[
          'text-center  transition-all text-base lg:text-lg px-4 pb-2',
          activeTab === tab.id
            ? 'text-[#3B2E90] border-b-2 border-[#3B2E90] font-bold -mb-[2px]'
            : 'text-grays-600',
        ]"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="px-4 lg:px-8 py-6 lg:py-8">
      <!-- Tab 1: Ingredients and Allergens (רכיבים ואלרגנים) -->
      <div
        v-if="activeTab === 'ingredients' && tabDataAvailability.ingredients"
        class="space-y-6 text-right"
      >
        <div
          v-for="section in ingredientsSections"
          :key="section.title"
          class="space-y-2"
        >
          <h3 class="text-lg font-bold text-grays-900">{{ section.title }}</h3>
          <p class="text-base text-grays-600 leading-relaxed">
            {{ section.text }}
          </p>
        </div>
      </div>

      <!-- Tab 2: Nutrition Label (סימון תזונתי) -->
      <div
        v-if="activeTab === 'nutrition' && tabDataAvailability.nutrition"
        class="text-right overflow-x-auto"
      >
        <table class="w-full border-collapse">
          <thead>
            <tr class="border-b border-grays-300">
              <th class="text-base font-medium text-grays-900 py-3 text-right">
                פרמטר
              </th>
              <th
                v-for="column in nutritionColumns"
                :key="column.basis"
                class="text-base text-grays-600 py-3 text-right"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in nutritionTableRows"
              :key="row.label"
              class="border-b border-grays-300"
            >
              <td class="text-base font-medium text-grays-900 py-3">
                {{ row.label }}
              </td>
              <td
                v-for="column in nutritionColumns"
                :key="column.basis"
                class="text-base text-grays-600 py-3"
              >
                {{ row[column.basis] || "-" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tab 3: Usage Instructions and Warnings (הוראות שימוש ואזהרות) -->
      <div
        v-if="activeTab === 'usage' && tabDataAvailability.usage"
        class="space-y-6 text-right"
      >
        <div
          v-for="section in usageSections"
          :key="section.title"
          class="space-y-2"
        >
          <h3 class="text-lg font-bold text-grays-900">{{ section.title }}</h3>
          <p class="text-base text-grays-600 leading-relaxed">
            {{ section.text }}
          </p>
        </div>
      </div>

      <!-- Tab 4: General Details (פרטים כלליים) -->
      <div
        v-if="activeTab === 'general' && tabDataAvailability.general"
        class="text-right"
      >
        <div class="space-y-2">
          <div class="flex justify-between py-3 border-b border-grays-300">
            <p class="text-base font-medium text-grays-900">פרמטר</p>
            <p class="text-base text-grays-600">ערך</p>
          </div>
          <div
            v-for="row in generalRows"
            :key="row.label"
            class="flex justify-between py-3 border-b border-grays-300"
          >
            <p class="text-base font-medium text-grays-900">{{ row.label }}</p>
            <p class="text-base text-grays-600">{{ row.value }}</p>
          </div>
        </div>
      </div>
    </div>
    <small class="text-xs text-grays-500 text-right"
      >אין להסתמך על הפירוט המופיע באתר על מרכיבי המוצר, יתכנו טעויות או אי
      התאמות במידע, הנתונים המדויקים מופיעים על גבי המוצר. יש לבדוק שוב את
      הנתונים על גבי אריזת המוצר לפני השימוש.
    </small>
  </div>
</template>

<script>
export default {
  name: "modal-product-tabs",
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Active tab state
      activeTab: "nutrition",
      selectedBarcodeIndex: 0,

      // Tabs configuration
      tabs: [
        { id: "ingredients", label: "רכיבים ואלרגנים" },
        { id: "nutrition", label: "סימון תזונתי" },
        { id: "usage", label: "הוראות שימוש ואזהרות" },
        { id: "general", label: "פרטים כלליים" },
      ],
    };
  },
  computed: {
    barcodeOptions() {
      const barcodes = this.product?.product?.barcodes || [];
      return barcodes.map((barcode, index) =>
        barcode.label && barcode.label.trim() !== ""
          ? barcode.label
          : `אופציה ${index + 1}`
      );
    },

    // Get nutritional values from the selected barcode
    nutritionalValues() {
      if (!this.product?.product?.barcodes?.length) {
        return [];
      }

      const barcode = this.product.product.barcodes[this.selectedBarcodeIndex];
      return barcode?.nutritional_values?.length
        ? barcode.nutritional_values
        : [];
    },

    // Format nutritional data for display
    nutritionRows() {
      if (!this.nutritionalValues.length) {
        return [];
      }

      return this.nutritionalValues
        .map((item) => {
          const amount = item.amount || "";
          const unit = item.unit || "";
          const value =
            amount && unit ? `${amount} ${unit}` : amount || unit || "";

          return {
            label: item.nutritional_field?.label || "",
            value: value,
          };
        })
        .filter((row) => row.label || row.value); // Filter out empty rows
    },

    // Get unique per_basis values (columns)
    nutritionColumns() {
      const basisSet = new Set();

      this.nutritionalValues.forEach((item) => {
        if (item.per_basis) {
          basisSet.add(item.per_basis);
        }
      });

      const columns = [];
      const labels = {
        per_100g: "ל-100 גרם",
        per_serving: "למנה",
      };

      basisSet.forEach((basis) => {
        columns.push({
          basis: basis,
          label: labels[basis] || basis,
        });
      });

      // Sort columns: per_100g first, then per_serving
      return columns.sort((a, b) => {
        if (a.basis === "per_100g") return -1;
        if (b.basis === "per_100g") return 1;
        return 0;
      });
    },

    // Format nutritional data as table rows with columns
    nutritionTableRows() {
      if (!this.nutritionalValues.length) {
        return [];
      }

      // Group by nutritional field label
      const rowsMap = new Map();

      this.nutritionalValues.forEach((item) => {
        const label = item.nutritional_field?.label || "";
        if (!label) return;

        if (!rowsMap.has(label)) {
          rowsMap.set(label, { label });
        }

        const row = rowsMap.get(label);
        const rawAmount = item.amount;
        const unit = item.unit || "";
        // Format amount: strip trailing zeros ("203.000" → "203", "7.700" → "7.7")
        const amount = rawAmount ? String(parseFloat(rawAmount)) : "";
        const value =
          amount && unit ? `${amount} ${unit}` : amount || unit || "";

        if (item.per_basis && value) {
          // Use per_basis from the item directly — works for both
          // GS1 format (Energy_per_100_grams) and legacy format (calories, fat_total, etc.)
          row[item.per_basis] = value;
        }
      });

      return Array.from(rowsMap.values()).filter((row) => row.label);
    },

    // Dynamic ingredients data from API
    ingredientsSections() {
      const sections = [];
      const metadata = this.product?.product?.metadata;

      // Parse product_components once
      const rawComponents = metadata?.product_components;
      let componentsData = null;
      if (rawComponents) {
        try {
          componentsData =
            typeof rawComponents === "string"
              ? JSON.parse(rawComponents)
              : rawComponents;
        } catch (e) {
          console.error("Error parsing product_components:", e);
        }
      }

      // Section 1: רכיבים — prefer flat metadata.ingredients, fallback to product_components
      if (metadata?.ingredients) {
        sections.push({ title: "רכיבים", text: metadata.ingredients });
      } else if (componentsData?.Ingredient_Sequence_and_Name) {
        sections.push({
          title: "רכיבים",
          text: componentsData.Ingredient_Sequence_and_Name,
        });
      }

      // Section 2: מכיל - prefer nutrition_flags, fallback to product_components
      const allergenContains = this.getAllergensByGroup("allergen_contains");
      if (allergenContains.length > 0) {
        sections.push({
          title: "מכיל",
          text: allergenContains.map((item) => item.label).join(", "),
        });
      } else if (componentsData?.Allergen_Type_Code_and_Containment) {
        const containmentItems =
          componentsData.Allergen_Type_Code_and_Containment.filter(
            (item) => item.value && item.value.trim() !== ""
          );
        if (containmentItems.length > 0) {
          sections.push({
            title: "מכיל",
            text: containmentItems.map((item) => item.value).join(", "),
          });
        }
      }

      // Section 3: עלול להכיל - prefer nutrition_flags, fallback to product_components
      const allergenMayContain = this.getAllergensByGroup(
        "allergen_may_contain"
      );
      if (allergenMayContain.length > 0) {
        sections.push({
          title: "עלול להכיל",
          text: allergenMayContain.map((item) => item.label).join(", "),
        });
      } else if (
        componentsData?.Allergen_Type_Code_and_Containment_May_Contain
      ) {
        const mayContainItems =
          componentsData.Allergen_Type_Code_and_Containment_May_Contain.filter(
            (item) => item.value && item.value.trim() !== ""
          );
        if (mayContainItems.length > 0) {
          sections.push({
            title: "עלול להכיל",
            text: mayContainItems.map((item) => item.value).join(", "),
          });
        }
      }

      // Section 4: מידע אלרגנים from Allergen_Displayed (product_components)
      if (componentsData?.Allergen_Displayed) {
        const allergenDisplayedItems = componentsData.Allergen_Displayed.filter(
          (item) => item.value && item.value.trim() !== ""
        );
        if (allergenDisplayedItems.length > 0) {
          sections.push({
            title: "מידע אלרגנים",
            text: allergenDisplayedItems.map((item) => item.value).join(", "),
          });
        }
      }

      // Fallback: if no structured allergen sections were added, use flat metadata.allergens
      const hasStructuredAllergens = sections.some((s) =>
        ["מכיל", "עלול להכיל", "מידע אלרגנים"].includes(s.title)
      );
      if (!hasStructuredAllergens && metadata?.allergens) {
        sections.push({ title: "אלרגנים", text: metadata.allergens });
      }

      return sections;
    },

    // Dynamic usage instructions and warnings data from API
    usageSections() {
      const sections = [];

      // Section 1: הוראות שימוש from product.metadata.storage_instructions
      const storageInstructions =
        this.product?.product?.metadata?.storage_instructions;
      if (storageInstructions) {
        sections.push({
          title: "הוראות שימוש",
          text: storageInstructions,
        });
      }

      // Section 2: אזהרות from product.metadata.precautionary_statement
      const precautionaryStatement =
        this.product?.product?.metadata?.precautionary_statement;
      if (precautionaryStatement) {
        sections.push({
          title: "אזהרות",
          text: precautionaryStatement,
        });
      }

      return sections;
    },

    // Placeholder data for general details (to be replaced later)
    generalRows() {
      return [
        { label: "ארץ יצור", value: "מיוצר בישראל 🇮🇱" },
        { label: "מותג / יצרן", value: "סנו" },
        { label: "תכולה", value: "4 ליטר" },
        { label: "סוג", value: "לא רלוונטי" },
        { label: "מכיל", value: "אין" },
      ];
    },

    // Tab data availability checks
    tabDataAvailability() {
      return {
        nutrition: this.nutritionTableRows.length > 0,
        ingredients: this.ingredientsSections.length > 0,
        usage: this.usageSections.length > 0,
        general: false, // Hidden for now
      };
    },

    // Filter tabs based on available data
    availableTabs() {
      return this.tabs.filter((tab) => this.tabDataAvailability[tab.id]);
    },

    // Check if we should show the component at all
    shouldShowTabs() {
      return this.availableTabs.length > 0;
    },
  },
  methods: {
    // Helper method to get allergens by nutrition_flag group
    getAllergensByGroup(group) {
      if (!this.product?.product?.barcodes?.length) {
        return [];
      }

      const barcode = this.product.product.barcodes[this.selectedBarcodeIndex];
      if (!barcode?.nutrition_flags?.length) {
        return [];
      }

      const allergens = barcode.nutrition_flags
        .filter((flag) => flag.nutrition_flag?.group === group)
        .map((flag) => ({
          label: flag.nutrition_flag.label,
          key: flag.nutrition_flag.key,
        }))
        .filter((item) => item.label);

      // Remove duplicates based on label
      return allergens.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.label === item.label)
      );
    },
  },
  watch: {
    product(newProduct, oldProduct) {
      if (newProduct?.id !== oldProduct?.id) {
        this.selectedBarcodeIndex = 0;
        this.$nextTick(() => {
          this.activeTab =
            this.availableTabs.length > 0
              ? this.availableTabs[0].id
              : "nutrition";
        });
      }
    },
  },
  mounted() {
    // Set the first available tab as active
    if (this.availableTabs.length > 0) {
      this.activeTab = this.availableTabs[0].id;
    }
  },
};
</script>
